<template>
  <div class="p-4">
    <h1 class="text-2xl font-bold mb-6">Vue + React Nested State Demo</h1>

    <div class="flex items-start gap-3">
      <div class="bg-gray-100 p-4 rounded-lg mt-6 w-1/3">
        <h3 class="text-lg font-semibold !m-0 mb-3">State Management Test</h3>
        <p>This demo tests how Vue's mutable nested state updates work with React's immutable approach.</p>
        <p class="mt-2">
          Last action: <strong>{{ lastAction }}</strong>
        </p>

        <div class="mt-4 p-3 bg-white rounded border border-gray-200">
          <h4 class="font-medium">Current State Object:</h4>
          <pre class="bg-gray-50 p-2 mt-2 text-xs overflow-auto">{{ JSON.stringify(state, null, 2) }}</pre>
        </div>
      </div>

      <!-- Vue Counter -->
      <div class="p-5 border-2 border-green-500 rounded-lg my-5">
        <h3 class="text-xl font-bold !m-0 mb-3">Vue Counter</h3>

        <div class="my-5">
          <div class="text-lg">
            Primary Count: <strong>{{ state.counts.primary }}</strong>
          </div>
          <div class="text-lg">
            Secondary Count: <strong>{{ state.counts.secondary }}</strong>
          </div>
          <div class="text-sm text-gray-600 mt-2">Last updated: {{ state.meta.lastUpdated }}</div>
        </div>

        <div class="flex gap-3">
          <button @click="incrementPrimaryInVue" class="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600">
            Increment Primary
          </button>
          <button
            @click="incrementSecondaryInVue"
            class="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
          >
            Increment Secondary
          </button>
          <button @click="resetInVue" class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">Reset</button>
        </div>
      </div>

      <!-- React Counter -->
      <Vue2React :component="Counter" :props="counterProps" />
    </div>
  </div>
</template>

<script>
import Vue2React from '@/_new/common/components/Vue2React.vue';
import { Counter } from '@/_new/common/components/Counter';

export default {
  components: { Vue2React },
  data() {
    return {
      state: {
        counts: {
          primary: 0,
          secondary: 0,
        },
        meta: {
          lastUpdated: new Date().toLocaleTimeString(),
        },
      },
      lastAction: 'None',
      Counter,
    };
  },

  computed: {
    counterProps() {
      return {
        state: this.state,
        onIncrementPrimary: () => {
          // This is a mutable update to nested state in Vue
          this.state.counts.primary += 1;
          this.state.meta.lastUpdated = new Date().toLocaleTimeString();
          this.lastAction = 'Primary Increment from React';
        },
        onIncrementSecondary: () => {
          // This is a mutable update to nested state in Vue
          this.state.counts.secondary += 1;
          this.state.meta.lastUpdated = new Date().toLocaleTimeString();
          this.lastAction = 'Secondary Increment from React';
        },
        onReset: () => {
          // This is a mutable update to nested state in Vue
          this.state.counts.primary = 0;
          this.state.counts.secondary = 0;
          this.state.meta.lastUpdated = new Date().toLocaleTimeString();
          this.lastAction = 'Reset from React';
        },
        title: 'React Counter (with state from Vue)',
      };
    },
  },

  methods: {
    incrementPrimaryInVue() {
      // Direct mutation of nested state
      this.state.counts.primary += 1;
      this.state.meta.lastUpdated = new Date().toLocaleTimeString();
      this.lastAction = 'Primary Increment from Vue';
    },

    incrementSecondaryInVue() {
      // Direct mutation of nested state
      this.state.counts.secondary += 1;
      this.state.meta.lastUpdated = new Date().toLocaleTimeString();
      this.lastAction = 'Secondary Increment from Vue';
    },

    resetInVue() {
      // Direct mutation of nested state
      this.state.counts.primary = 0;
      this.state.counts.secondary = 0;
      this.state.meta.lastUpdated = new Date().toLocaleTimeString();
      this.lastAction = 'Reset from Vue';
    },
  },
};
</script>
